import axios from 'axios';
import {
  clearGuestCart,
  getGuestCartCount,
  getGuestCartItems,
  normalizeOptionIds,
  normalizeUploadIds,
  removeGuestCartItem,
  updateGuestCartItem,
  upsertGuestCartItem,
  writeGuestCart,
} from '../utils/guestCart';
import {
  getAccessToken,
  setAccessToken,
  getRefreshToken,
  setRefreshToken,
  getUserBlob,
  clearAllAuth,
} from '../utils/authStorage';
import { hasRole } from '../utils/roles';

const resolveDevApiBaseUrl = () => {
  // خالی (undefined/null/'') → relative /api (tک-origin). فقط وقتی مقدار صریح آدرس بدهیم.
  const rawEnv = (process.env.REACT_APP_API_URL ?? '').trim();
  const raw = rawEnv || (typeof window === 'undefined' ? 'http://localhost:8000/api' : '/api');
  if (typeof window === 'undefined') return raw;
  try {
    const u = new URL(raw, window.location.href);
    const isLocalBackend = u.hostname === 'localhost' || u.hostname === '127.0.0.1' || u.hostname === '0.0.0.0';
    const h = window.location.hostname || '';
    const isLocalFrontend = h === 'localhost' || h === '127.0.0.1' || h === '0.0.0.0';
    if (process.env.NODE_ENV !== 'production' && isLocalBackend && !isLocalFrontend && h) {
      const protocol = window.location.protocol === 'https:' ? 'https:' : 'http:';
      return `${protocol}//${h}:8000/api`;
    }
    return raw;
  } catch (_) {
    return raw;
  }
};

const BASE_URL = resolveDevApiBaseUrl();
const LS_BACKEND_COOLDOWN_UNTIL = 'kadolar_backend_cooldown_until_v1';
const LS_BACKEND_LAST_OK = 'kadolar_backend_last_ok_v1';
const LS_DEVICE_ID = 'kadolar_device_id_v1';
let backendCooldownUntil = (() => {
  try {
    const raw = localStorage.getItem(LS_BACKEND_COOLDOWN_UNTIL);
    const ts = raw ? parseInt(raw, 10) : 0;
    return Number.isFinite(ts) ? ts : 0;
  } catch {
    return 0;
  }
})();

const isDevLocalBackend = (() => {
  if (process.env.NODE_ENV === 'production') return false;
  try {
    const u = new URL(BASE_URL);
    return u.hostname === 'localhost' || u.hostname === '127.0.0.1';
  } catch {
    return false;
  }
})();

const getFullRequestUrl = (config) => {
  try {
    const raw = config?.url || '';
    if (/^https?:\/\//i.test(raw)) return raw;
    const base = config?.baseURL || BASE_URL;
    return new URL(raw, base).toString();
  } catch (_) {
    return null;
  }
};

const readCookie = (name) => {
  try {
    const m = document.cookie
      .split('; ')
      .find((c) => c.startsWith(`${name}=`));
    return m ? decodeURIComponent(m.split('=')[1]) : null;
  } catch (_) {
    return null;
  }
};


const setBackendCooldown = (untilTs) => {
  backendCooldownUntil = untilTs;
  try {
    localStorage.setItem(LS_BACKEND_COOLDOWN_UNTIL, String(untilTs));
  } catch {}
};

const markBackendOk = () => {
  try {
    localStorage.setItem(LS_BACKEND_LAST_OK, String(Date.now()));
    localStorage.removeItem(LS_BACKEND_COOLDOWN_UNTIL);
  } catch {}
  backendCooldownUntil = 0;
};

const api = axios.create({
  baseURL: BASE_URL,
  timeout: 15000,
  withCredentials: true, // ارسال کوکی session برای آپلود مهمان و دانلود فایل‌های موقت (کراس‌اورجین 3000→8000)
  headers: {
    'Content-Type': 'application/json',
  },
});

// Phase 18: token/refresh/user keys are now owned by authStorage.js.
// The constant is kept (and suppressed) only so any legacy code that still
// references the list of keys does not silently lose the names.
// eslint-disable-next-line no-unused-vars
const AUTH_STORAGE_KEYS = ['token', 'user', 'refresh_token'];
let authFailureHandledAt = 0;

const clearAuthStorage = (reason = 'auth_error') => {
  try {
    clearAllAuth();
  } catch (_) {}
  try {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('kadolar:auth-invalid', { detail: { reason } }));
    }
  } catch (_) {}
};

const AUTH_REQUIRED_PATHS = [
  '/auth/profile',
  '/auth/addresses',
  '/auth/logout',
  '/orders',
  '/orders/cart',
  '/orders/cart/add',
  '/orders/cart/items',
  '/orders/create',
  '/products/wishlist',
  '/chat/rooms',
  '/chat/messages',
  '/chat/create-room',
  '/chat/upload',
  // Staff chat endpoints (همه‌ی زیرمسیرها: metrics/export هم پوشش داده می‌شوند)
  '/staff/chat/',
  '/notifications',
  '/analytics',
  '/settings/system/manage',
  '/settings/banners/manage',
  // P1 review fix: این‌ها احراز هویت‌شده‌اند ولی قبلاً توکن نمی‌گرفتند →
  // هر بار 401 + refresh + retry (دو درخواست به‌جای یک)
  '/finance/',
  '/partners/',
  '/auth/change-password',
  '/auth/bank-account',
];

const OPTIONAL_AUTH_PATHS = [
  '/support/temp-media',
];

const needsAuthHeader = (url) => {
  try {
    const u = typeof url === 'string' ? url : (url?.url || '');
    const path = u.split('?')[0];
    return AUTH_REQUIRED_PATHS.some(prefix => path.startsWith(prefix));
  } catch {
    return false;
  }
};

const isOptionalAuthPath = (url) => {
  try {
    const u = typeof url === 'string' ? url : (url?.url || '');
    const path = u.split('?')[0];
    return OPTIONAL_AUTH_PATHS.some(prefix => path.startsWith(prefix));
  } catch {
    return false;
  }
};

const isTokenExpired = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1] || ''));
    const expSec = payload?.exp;
    if (!expSec) return false;
    const nowSec = Math.floor(Date.now() / 1000);
    return expSec <= nowSec;
  } catch {
    return true; // malformed token -> treat as expired
  }
};

const makeUuidV4 = () => {
  try {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  } catch {
    return null;
  }
};

export const getDeviceId = () => {
  try {
    const existing = localStorage.getItem(LS_DEVICE_ID);
    if (existing && typeof existing === 'string' && existing.trim()) return existing.trim();
  } catch {}

  let next = null;
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      next = crypto.randomUUID();
    } else if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
      next = makeUuidV4();
    }
  } catch {}
  if (!next) next = `dev-${Date.now()}-${Math.random().toString(16).slice(2)}`;

  try {
    localStorage.setItem(LS_DEVICE_ID, next);
  } catch {}
  return next;
};

const hasValidAuthToken = () => {
  try {
    const token = getAccessToken();
    return Boolean(token) && !isTokenExpired(token);
  } catch {
    return false;
  }
};

const emitCartChanged = () => {
  try {
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem('kadolar_cart_updated_at', String(Date.now()));
    } catch (_) {}
    window.dispatchEvent(new Event('kadolar:cart-changed'));
  } catch (_) {}
};

const LS_CART_MERGE_BACKUP = 'kadolar_cart_merge_backup_v1';
const LS_CART_MERGE_RESULT = 'kadolar_cart_merge_result_v1';

const readMergeBackup = () => {
  try {
    const raw = localStorage.getItem(LS_CART_MERGE_BACKUP);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (_) {
    return null;
  }
};

const writeMergeBackup = (snapshot) => {
  try {
    const safe = snapshot && typeof snapshot === 'object' ? snapshot : null;
    if (safe) localStorage.setItem(LS_CART_MERGE_BACKUP, JSON.stringify(safe));
    else localStorage.removeItem(LS_CART_MERGE_BACKUP);
  } catch (_) {}
};

const writeMergeResult = (result) => {
  try {
    const safe = result && typeof result === 'object' ? result : null;
    if (safe) localStorage.setItem(LS_CART_MERGE_RESULT, JSON.stringify(safe));
    else localStorage.removeItem(LS_CART_MERGE_RESULT);
  } catch (_) {}
};

const safeNumber = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const computeRequiredCustomFeatureTypeIds = (product) => {
  const opts = Array.isArray(product?.custom_feature_options) ? product.custom_feature_options : [];
  const requiredTypeIds = new Set();
  for (const o of opts) {
    const active = o?.is_active !== false && o?.feature_type_is_active !== false && o?.feature_type?.is_active !== false;
    if (!active) continue;
    const tid = o?.feature_type ?? o?.feature_type_id ?? o?.feature_type?.id;
    const n = safeNumber(tid, null);
    if (n && n > 0) requiredTypeIds.add(n);
  }
  return requiredTypeIds;
};

const validateGuestCustomFeatureSelection = (product, optionIds) => {
  const requiredTypeIds = computeRequiredCustomFeatureTypeIds(product);
  if (requiredTypeIds.size === 0) return { ok: true, normalized: normalizeOptionIds(optionIds) };
  const ids = normalizeOptionIds(optionIds);
  if (!ids.length) return { ok: false, error: 'لطفاً برای ویژگی‌های خاص انتخاب کنید.' };
  const opts = Array.isArray(product?.custom_feature_options) ? product.custom_feature_options : [];
  const byId = new Map();
  for (const o of opts) {
    const id = safeNumber(o?.id, null);
    if (!id) continue;
    byId.set(id, o);
  }
  const selectedTypeIds = new Map();
  for (const id of ids) {
    const opt = byId.get(id);
    if (!opt) return { ok: false, error: 'یکی از گزینه‌های ویژگی خاص معتبر نیست.' };
    const active = opt?.is_active !== false && opt?.feature_type_is_active !== false && opt?.feature_type?.is_active !== false;
    if (!active) return { ok: false, error: 'یکی از گزینه‌های ویژگی خاص معتبر نیست.' };
    const tid = safeNumber(opt?.feature_type ?? opt?.feature_type_id ?? opt?.feature_type?.id, null);
    if (!tid) return { ok: false, error: 'یکی از گزینه‌های ویژگی خاص معتبر نیست.' };
    selectedTypeIds.set(tid, (selectedTypeIds.get(tid) || 0) + 1);
  }
  for (const tid of requiredTypeIds) {
    if (!selectedTypeIds.has(tid)) return { ok: false, error: 'لطفاً برای ویژگی‌های خاص انتخاب کنید.' };
  }
  for (const count of selectedTypeIds.values()) {
    if (count > 1) return { ok: false, error: 'برای هر ویژگی فقط یک گزینه قابل انتخاب است.' };
  }
  return { ok: true, normalized: ids };
};

const validateGuestCustomUploads = (product, uploadIds) => {
  const reqs = Array.isArray(product?.upload_requirements) ? product.upload_requirements.filter((r) => r && r.is_active !== false) : [];
  if (!reqs.length) {
    if (Array.isArray(uploadIds) && uploadIds.length) return { ok: false, error: 'این محصول نیازی به آپلود ندارد.' };
    return { ok: true, normalized: [] };
  }
  const ids = normalizeUploadIds(uploadIds);
  const hasRequired = reqs.some((r) => r?.is_required);
  if (hasRequired && !ids.length) return { ok: false, error: 'آپلود فایل الزامی است.' };
  const totalMin = reqs.reduce((sum, r) => sum + (r?.is_required ? Number(r?.min_count || 0) : 0), 0);
  const totalMax = reqs.reduce((sum, r) => sum + Number(r?.max_count || 0), 0);
  if (ids.length < totalMin) return { ok: false, error: `حداقل ${totalMin} فایل لازم است.` };
  if (totalMax && ids.length > totalMax) return { ok: false, error: `حداکثر ${totalMax} فایل مجاز است.` };
  return { ok: true, normalized: ids };
};

const computeGuestUnitPriceAndOptions = (product, optionIds) => {
  const base = safeNumber(product?.final_price ?? product?.discount_price ?? product?.price, 0);
  const ids = normalizeOptionIds(optionIds);
  const opts = Array.isArray(product?.custom_feature_options) ? product.custom_feature_options : [];
  const byId = new Map();
  for (const o of opts) {
    const id = safeNumber(o?.id, null);
    if (!id) continue;
    byId.set(id, o);
  }
  let unit = base;
  const picked = [];
  for (const id of ids) {
    const o = byId.get(id);
    if (!o) continue;
    const active = o?.is_active !== false && o?.feature_type_is_active !== false && o?.feature_type?.is_active !== false;
    if (!active) continue;
    unit += safeNumber(o?.price_adjustment, 0);
    picked.push({
      id,
      feature_type: safeNumber(o?.feature_type ?? o?.feature_type_id ?? o?.feature_type?.id, null),
      feature_type_name: String(o?.feature_type_name || o?.feature_type?.name || '').trim(),
      option_name: String(o?.option_name || '').trim(),
      price_adjustment: String(o?.price_adjustment ?? 0),
    });
  }
  return { unit_price: unit, custom_feature_options: picked, normalized_option_ids: ids };
};

const COUPON_UPDATED_AT_KEY = 'kadolar_coupon_updated_at';

let couponCodeMem = '';
let couponSnapshotMem = null;

const emitCouponChanged = () => {
  try {
    if (typeof window === 'undefined') return;
    try {
      localStorage.setItem(COUPON_UPDATED_AT_KEY, String(Date.now()));
    } catch (_) {}
    window.dispatchEvent(new Event('kadolar:coupon-changed'));
  } catch (_) {}
};

export const couponStore = {
  getCode: () => {
    try {
      return String(couponCodeMem || '').trim() || '';
    } catch (_) {
      return '';
    }
  },
  setCode: (code) => {
    try {
      const c = String(code || '').trim();
      couponCodeMem = c;
      emitCouponChanged();
    } catch (_) {}
  },
  clear: () => {
    try {
      couponCodeMem = '';
      couponSnapshotMem = null;
      emitCouponChanged();
    } catch (_) {}
  },
  getSnapshot: () => {
    try {
      return couponSnapshotMem && typeof couponSnapshotMem === 'object' ? couponSnapshotMem : null;
    } catch (_) {
      return null;
    }
  },
  setSnapshot: (snapshot) => {
    try {
      couponSnapshotMem = snapshot && typeof snapshot === 'object' ? snapshot : null;
      emitCouponChanged();
    } catch (_) {}
  },
};

let refreshPromise = null;

api.interceptors.request.use(
  async (config) => {
    try {
      const headers = (config.headers = config.headers || {});
      const isFormDataPayload = typeof FormData !== 'undefined' && config.data instanceof FormData;
      if (config.data != null && !isFormDataPayload && !String(config.method || 'get').toLowerCase().match(/^(get|head)$/)) {
        const ct = String(headers['Content-Type'] ?? headers['content-type'] ?? '').toLowerCase();
        if (!ct) headers['Content-Type'] = 'application/json';
      }

      // CSRF: فقط برای POST/PUT/PATCH، اگر کوکی csrftoken روی origin باشد (مثلاً کاربر
      // قبل از ورود به فرانت، ادمین را باز کرده باشد)، هدر X-CSRFToken را از کوکی
      // بساز تا Django CSRF middleware قبول کند. API های JWT/OTP همه csrf_exempt هستند؛
      // این فقط برای فرم‌های HTML/کوکی‌سشن (پنل ادمین) لازم است.
      const m = String(config.method || 'get').toLowerCase();
      if (m !== 'get' && m !== 'head') {
        const csrfToken = readCookie('csrftoken');
        if (csrfToken && !headers['X-CSRFToken']) {
          headers['X-CSRFToken'] = csrfToken;
        }
      }

      if (isDevLocalBackend && backendCooldownUntil > Date.now()) {
        const method = String(config?.method || 'get').toLowerCase();
        const fullUrl = getFullRequestUrl(config);
        if (fullUrl) {
          const baseOrigin = new URL(BASE_URL).origin;
          const reqOrigin = new URL(fullUrl).origin;
          if (baseOrigin === reqOrigin) {
            if (method === 'get') {
              return Promise.reject(new axios.CanceledError('backend_cooldown'));
            }
          }
        }
      }
      const url = config.url;
      const isRequired = needsAuthHeader(url);
      const isOptional = !isRequired && isOptionalAuthPath(url);
      if (!isRequired && !isOptional) {
        return config;
      }
      const token = getAccessToken();
      const refresh = getRefreshToken();
      if (token && !isTokenExpired(token)) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
        return config;
      }
      if (isOptional && !refresh) {
        return config;
      }
      if (refresh) {
        if (!refreshPromise) {
          const refreshUrl = `${BASE_URL.replace(/\/$/, '')}/auth/token/refresh/`;
          refreshPromise = axios
            .post(
              refreshUrl,
              { refresh, device_id: getDeviceId() },
              { timeout: 15000, headers: { 'Content-Type': 'application/json' } }
            )
            .then((res) => {
              const nextToken = res && res.data && res.data.access;
              const nextRefresh = res && res.data && res.data.refresh;
              if (nextToken) {
                setAccessToken(nextToken);
                if (nextRefresh) {
                  setRefreshToken(nextRefresh);
                }
                return nextToken;
              }
              throw new Error('missing_access_token');
            })
            .catch((err) => {
              clearAuthStorage('refresh_failed');
              return Promise.reject(err);
            })
            .finally(() => {
              refreshPromise = null;
            });
        }
        try {
          const next = await refreshPromise;
          if (next) {
            config.headers = config.headers || {};
            config.headers.Authorization = `Bearer ${next}`;
          }
        } catch (_) {
          if (isOptional) {
            return config;
          }
          const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
          const isAuthPage = currentPath === '/login' || currentPath === '/register';
          if (!isAuthPage) {
            try {
              const now = Date.now();
              if (!authFailureHandledAt || now - authFailureHandledAt > 5000) {
                authFailureHandledAt = now;
                // P3 review fix: به‌جای full-page reload، event اپ‌سطح
                // dispatch می‌شود (AuthContext گوش می‌دهد) — state اپ نمی‌سوزد.
                try { window.dispatchEvent(new CustomEvent('kadolar:auth-invalid', { detail: { reason: 'unauthorized' } })); } catch (_) {}
                // P3 review fix: event اپ‌سطح قبل از ناوبری
                try { window.dispatchEvent(new CustomEvent('kadolar:auth-invalid', { detail: { reason: 'unauthorized' } })); } catch (_) {}
                window.location.href = '/login';
              }
            } catch (_) {}
          }
          return Promise.reject({ message: 'لطفاً وارد حساب کاربری خود شوید', type: 'auth_error', status: 401 });
        }
        return config;
      }
      if (isOptional) {
        return config;
      }
      const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
      const isAuthPage = currentPath === '/login' || currentPath === '/register';
      clearAuthStorage('unauthorized');
      if (!isAuthPage) {
        try {
          const now = Date.now();
          if (!authFailureHandledAt || now - authFailureHandledAt > 5000) {
            authFailureHandledAt = now;
            window.location.href = '/login';
          }
        } catch (_) {}
      }
      return Promise.reject({ message: 'لطفاً وارد حساب کاربری خود شوید', type: 'auth_error', status: 401 });
    } catch (e) {
      return config;
    }
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => {
    if (isDevLocalBackend) {
      try {
        const fullUrl = getFullRequestUrl(response?.config);
        if (fullUrl) {
          const baseOrigin = new URL(BASE_URL).origin;
          const respOrigin = new URL(fullUrl).origin;
          if (baseOrigin === respOrigin) {
            markBackendOk();
          }
        }
      } catch {}
    }
    return response;
  },
  async (error) => {
    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }

    // Handle network errors
    if (!error.response) {
      const code = String(error?.code || '').toUpperCase();
      const msg = String(error?.message || '').toLowerCase();
      const isTimeout = code === 'ECONNABORTED' || msg.includes('timeout');
      if (isDevLocalBackend) {
        if (!isTimeout) {
          setBackendCooldown(Date.now() + 8000);
        }
      }
      return Promise.reject({
        message: 'خطا در اتصال به سرور. لطفاً اتصال اینترنت خود را بررسی کنید',
        type: 'network_error'
      });
    }

    // Handle HTTP errors
    const { status } = error.response;
    
    // Handle JSON parse errors
    if (error.response.headers['content-type']?.includes('text/html')) {
      try {
        const fullUrl = getFullRequestUrl(error?.config);
        const len = (() => {
          try {
            const d = error?.response?.data;
            if (typeof d === 'string') return d.length;
            return d ? JSON.stringify(d).length : 0;
          } catch {
            return 0;
          }
        })();
        console.error('Server returned HTML instead of JSON:', { url: fullUrl, status, length: len });
      } catch (_) {}
      return Promise.reject({
        message: 'خطای سرور. لطفاً بعداً تلاش کنید',
        type: 'server_error'
      });
    }

    if (status === 401) {
      const originalRequest = error.config || {};
      // پلِ هویت staff: اگر 401 شد، فقط reject بده (مهمان/مشتری است، staff نیست)
      // بدون refresh، بدون redirect به /login
      if (originalRequest._noAuthGuard) {
        return Promise.reject({ message: 'session bridge denied', type: 'auth_error', status: 401 });
      }
      const refresh = getRefreshToken();
      const isProtected = needsAuthHeader(originalRequest.url);
      const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
      const isAuthPage = currentPath === '/login' || currentPath === '/register';
      if (refresh && !originalRequest._retry401Refresh) {
        originalRequest._retry401Refresh = true;
        try {
          if (!refreshPromise) {
            const refreshUrl = `${BASE_URL.replace(/\/$/, '')}/auth/token/refresh/`;
            refreshPromise = axios
              .post(refreshUrl, { refresh, device_id: getDeviceId() }, { timeout: 15000 })
              .then((res) => {
                const nextToken = res && res.data && res.data.access;
                const nextRefresh = res && res.data && res.data.refresh;
                if (nextToken) {
                  setAccessToken(nextToken);
                  if (nextRefresh) {
                    setRefreshToken(nextRefresh);
                  }
                  return nextToken;
                }
                throw new Error('missing_access_token');
              })
              .catch((err) => {
                clearAuthStorage('refresh_failed');
                return Promise.reject(err);
              })
              .finally(() => {
                refreshPromise = null;
              });
          }
          const nextToken = await refreshPromise;
          if (nextToken) {
            originalRequest.headers = originalRequest.headers || {};
            originalRequest.headers.Authorization = `Bearer ${nextToken}`;
            return api(originalRequest);
          }
        } catch (_) {}
      }
      if (isProtected && !isAuthPage) {
        clearAuthStorage('unauthorized');
        try {
          const now = Date.now();
          if (!authFailureHandledAt || now - authFailureHandledAt > 5000) {
            authFailureHandledAt = now;
            window.location.href = '/login';
          }
        } catch (_) {}
        return Promise.reject({ message: 'لطفاً وارد حساب کاربری خود شوید', type: 'auth_error', status: 401 });
      }
      return Promise.reject({ message: 'لطفاً وارد حساب کاربری خود شوید', type: 'auth_error', status: 401 });
    }

    if (status === 403) {
      const originalRequest = error.config || {};
      const isProtected = needsAuthHeader(originalRequest.url);
      if (isProtected) {
        return Promise.reject({ message: 'شما اجازه دسترسی به این بخش را ندارید', type: 'permission_error', status: 403 });
      }
      return Promise.reject(error);
    }
    
    return Promise.reject(error);
  }
);

// Auth API calls
export const authAPI = {
  // Send OTP
  sendOTP: (phoneNumber, purpose) => {
    const body = { phone_number: phoneNumber };
    if (purpose) body.purpose = purpose;
    return api.post('/auth/send-verification/', body);
  },

  // پلِ هویت staff (تک‑origin) — هویت از سشن ادمین جنگو (کوکی)، خروجی JWT برای فرانت.
  // HIGH-2: حالا POST + CSRF است (نه GET). کوکی سشن به‌صورت خودکار ارسال می‌شود.
  // _noAuthGuard: در صورت 401، interceptor نه refresh و نه redirect به /login انجام دهد
  getSessionUser: () => {
    return api.post('/auth/session-user/', {}, { _noAuthGuard: true });
  },

  // HIGH-3: مبادله‌ی ticket یک‌بارمصرفِ cross-origin با JWT واقعی (در بدنهٔ POST،
  // نه در URL). برای فرانتِ cross-origin که از /staff?bridge_ticket=... وارد شد.
  exchangeBridge: (bridgeTicket) => {
    return api.post('/auth/bridge-exchange/', { bridge_ticket: bridgeTicket }, { _noAuthGuard: true });
  },

  // Verify OTP and login
  verifyOTP: (phoneNumber, otp) => {
    return api.post('/auth/otp-login/', { 
      phone: phoneNumber, 
      code: otp,
      device_id: getDeviceId(),
    });
  },

  // Register user
  register: (userData) => {
    const payload = userData && typeof userData === 'object' ? { ...userData } : {};
    if (!payload.device_id) payload.device_id = getDeviceId();
    return api.post('/auth/otp-register/', payload);
  },

  // Get user profile
  getProfile: () => {
    return api.get('/auth/profile/');
  },

  // Logout (blacklist refresh token on server)
  logout: () => {
    try {
      const refresh = getRefreshToken();
      if (!refresh) return Promise.resolve({ data: {} });
      return api.post('/auth/logout/', { refresh });
    } catch (_) {
      return Promise.resolve({ data: {} });
    }
  },

  // Update user profile
  updateProfile: (userData) => {
    return api.patch('/auth/profile/', userData);
  }
};

// Products API calls
export const productsAPI = {
  // Get all products with pagination and filters
  getProducts: (params = {}) => {
    return api.get('/products/', { params });
  },

  // Get single product
  getProduct: (id, params = undefined) => {
    return api.get(`/products/${id}/`, params ? { params } : undefined);
  },

  // Search products
  searchProducts: (query, filters = {}) => {
    return api.get('/products/search/', { 
      params: { q: query, ...filters } 
    });
  },

  // Get product categories
  getCategories: () => {
    return api.get('/products/categories/');
  },

  // Get single category by slug
  getCategory: (slug) => {
    return api.get(`/products/categories/${slug}/`);
  },

  getCategoryReviews: (slug) => {
    return api.get(`/products/categories/${slug}/reviews/`);
  },

  getShoghtanehStatus: () => {
    return api.get('/products/shoghtaneh/');
  },

  getCustomProductSettings: () => {
    return api.get('/products/custom-settings/');
  },

  getProductIntroductions: () => {
    return api.get('/products/introductions/');
  },

  getProductIntroduction: (slug) => {
    return api.get(`/products/introductions/${slug}/`);
  },

  // ── Reviews ──
  /** Get all approved reviews for a product. */
  getReviews: (productId) => {
    return api.get(`/products/${productId}/reviews/`);
  },
  /** Get the current user's review status for a product (can_review, has_delivered_order, existing_review). */
  getReviewStatus: (productId) => {
    return api.get(`/products/${productId}/review-status/`);
  },
  /** Create a new review for a product. */
  createReview: (productId, data) => {
    return api.post(`/products/${productId}/reviews/`, data);
  },
  /** Update the current user's own review. */
  updateReview: (productId, reviewId, data) => {
    return api.patch(`/products/${productId}/reviews/${reviewId}/`, data);
  },
  /** Delete the current user's own review. */
  deleteReview: (productId, reviewId) => {
    return api.delete(`/products/${productId}/reviews/${reviewId}/`);
  },
};

// Cart API calls
export const cartAPI = {
  getCart: async () => {
    if (!hasValidAuthToken()) {
      const guestItems = getGuestCartItems();
      const uniqueProductIds = Array.from(
        new Set(
          guestItems
            .map((it) => safeNumber(it?.product_id, null))
            .filter((n) => Number.isFinite(n) && n > 0)
        )
      );
      const productById = new Map();
      await Promise.all(
        uniqueProductIds.map(async (pid) => {
          try {
            const res = await productsAPI.getProduct(pid);
            productById.set(pid, res?.data || null);
          } catch (_) {
            productById.set(pid, null);
          }
        })
      );
      const detailedItems = [];
      for (const it of guestItems) {
        const pid = safeNumber(it?.product_id, null);
        if (!pid || pid <= 0) continue;
        const product = productById.get(pid);
        if (!product || product?.status === 'unpublished') continue;
        const computed = computeGuestUnitPriceAndOptions(product, it?.custom_feature_option_ids);
        // قیمت افزودهٔ آپلود مهمان هم محاسبه شود (هر نوع یک‌بار)
        let guestUploadExtra = 0;
        try {
          const reqs = Array.isArray(product?.upload_requirements) ? product.upload_requirements.filter((r) => r && r.is_active !== false) : [];
          const cs = product?.custom_settings || null;
          if (reqs.length && cs) {
            const seen = new Set();
            for (const r of reqs) {
              const k = r?.kind;
              if (!k || seen.has(k)) continue;
              seen.add(k);
              const price = Number(cs[`${k}_price`] || 0);
              const enabled = cs[`${k}_enabled`] !== false;
              if (enabled && Number.isFinite(price) && price > 0) guestUploadExtra += price;
            }
          }
        } catch (_) {}
        const unitWithUpload = computed.unit_price + guestUploadExtra;
        const qty = Math.max(1, Math.floor(Number(it?.quantity) || 1));
        detailedItems.push({
          id: String(it?.id || ''),
          product_id: pid,
          product,
          quantity: qty,
          custom_feature_option_ids: computed.normalized_option_ids,
          custom_feature_options: computed.custom_feature_options,
          custom_upload_ids: normalizeUploadIds(it?.custom_upload_ids),
          custom_upload_details: Array.isArray(it?.custom_upload_meta) ? it.custom_upload_meta : [],
          unit_price: unitWithUpload,
          effective_unit_price: unitWithUpload,
          total_price: unitWithUpload * qty,
          created_at: it?.created_at || null,
        });
      }
      const subtotal = detailedItems.reduce((sum, x) => sum + safeNumber(x?.total_price, 0), 0);
      return {
        data: {
          id: 'guest',
          items: detailedItems,
          items_count: detailedItems.reduce((sum, x) => sum + (Number(x?.quantity) || 0), 0),
          subtotal,
          shipping_cost: 0,
          discount_amount: 0,
          total_amount: subtotal,
          free_shipping_threshold: 0,
          amount_to_free_shipping: 0,
          is_free_shipping: false,
          total_price: subtotal,
          created_at: null,
          updated_at: null,
        },
      };
    }
    return api.get('/orders/cart/');
  },

  addToCart: async (productId, quantity = 1, customFeatureOptionIds = [], customUploadIds = [], customUploadMeta = [], customFieldValues = {}) => {
    if (!hasValidAuthToken()) {
      const pid = safeNumber(productId, null);
      if (!pid || pid <= 0) return Promise.reject(new Error('invalid_product_id'));
      const q = Math.max(1, Math.floor(Number(quantity) || 1));
      const res = await productsAPI.getProduct(pid);
      const product = res?.data || null;
      if (!product || product?.status === 'unpublished') {
        return Promise.reject(new Error('product_not_found'));
      }
      if (!(Number(product?.stock) > 0) || String(product?.status || '').toLowerCase() === 'out_of_stock') {
        return Promise.reject(new Error('out_of_stock'));
      }
      const maxAllowed = product?.allow_order_quantity ? Math.max(1, Math.floor(safeNumber(product?.max_order_quantity, 1))) : 1;
      const nextQty = maxAllowed === 1 ? 1 : Math.min(q, maxAllowed);
      const stock = safeNumber(product?.stock, 0);
      if (stock < nextQty) {
        return Promise.reject(new Error('insufficient_stock'));
      }
      const selection = validateGuestCustomFeatureSelection(product, customFeatureOptionIds);
      if (!selection.ok) {
        return Promise.reject(new Error(selection.error || 'invalid_custom_options'));
      }
      const uploadCheck = validateGuestCustomUploads(product, customUploadIds);
      if (!uploadCheck.ok) {
        return Promise.reject(new Error(uploadCheck.error || 'invalid_uploads'));
      }
      const uploadIds = uploadCheck.normalized;
      const meta = Array.isArray(customUploadMeta) ? customUploadMeta.filter((x) => x && Number.isFinite(Number(x.id))) : [];
      const fieldValues = (customFieldValues && typeof customFieldValues === 'object' && !Array.isArray(customFieldValues)) ? customFieldValues : {};
      const key = upsertGuestCartItem({
        productId: pid,
        quantity: nextQty,
        customFeatureOptionIds: selection.normalized,
        customUploadIds: uploadIds,
        customUploadMeta: meta.length ? meta : undefined,
        customFieldValues: fieldValues,
      });
      emitCartChanged();
      return { data: { message: 'added_to_guest_cart', cart_item: { id: key } } };
    }
    const payload = {
      product_id: productId,
      quantity,
    };
    if (Array.isArray(customFeatureOptionIds) && customFeatureOptionIds.length > 0) {
      payload.custom_feature_option_ids = customFeatureOptionIds;
    }
    if (Array.isArray(customUploadIds) && customUploadIds.length > 0) {
      payload.custom_upload_ids = customUploadIds;
    }
    if (customFieldValues && typeof customFieldValues === 'object' && Object.keys(customFieldValues).length > 0) {
      payload.custom_field_values = customFieldValues;
    }
    return api.post('/orders/cart/add/', payload).then((res) => {
      emitCartChanged();
      return res;
    });
  },

  updateCartItem: async (itemId, quantity, customFeatureOptionIds = undefined, customUploadIds = undefined) => {
    if (!hasValidAuthToken()) {
      const ok = updateGuestCartItem(itemId, { quantity, customFeatureOptionIds, customUploadIds });
      if (!ok) return Promise.reject(new Error('guest_item_not_found'));
      emitCartChanged();
      return { data: { message: 'guest_cart_updated' } };
    }
    const payload = { quantity };
    if (Array.isArray(customFeatureOptionIds)) {
      payload.custom_feature_option_ids = customFeatureOptionIds;
    }
    if (Array.isArray(customUploadIds)) {
      payload.custom_upload_ids = customUploadIds;
    }
    return api.put(`/orders/cart/items/${itemId}/update/`, payload).then((res) => {
      emitCartChanged();
      return res;
    });
  },

  removeFromCart: async (itemId) => {
    if (!hasValidAuthToken()) {
      const ok = removeGuestCartItem(itemId);
      if (!ok) return Promise.reject(new Error('guest_item_not_found'));
      emitCartChanged();
      return { data: { message: 'guest_cart_item_removed' } };
    }
    return api.delete(`/orders/cart/items/${itemId}/remove/`).then((res) => {
      emitCartChanged();
      return res;
    });
  },

  clearCart: async () => {
    if (!hasValidAuthToken()) {
      clearGuestCart();
      emitCartChanged();
      return { data: { message: 'guest_cart_cleared' } };
    }
    return api.delete('/orders/cart/clear/').then((res) => {
      emitCartChanged();
      return res;
    });
  },

  hasGuestCart: () => getGuestCartCount() > 0,

  getLastMergeResult: () => {
    try {
      const raw = localStorage.getItem(LS_CART_MERGE_RESULT);
      return raw ? JSON.parse(raw) : null;
    } catch (_) {
      return null;
    }
  },

  discardFailedMergeItems: () => {
    let result = null;
    try {
      const raw = localStorage.getItem(LS_CART_MERGE_RESULT);
      result = raw ? JSON.parse(raw) : null;
    } catch (_) {
      result = null;
    }
    const failures = result && Array.isArray(result.failures) ? result.failures : [];
    if (!failures.length) return { discarded: 0 };
    const items = getGuestCartItems();
    const dropIds = new Set();
    for (const f of failures) {
      const pid = safeNumber(f?.product_id, null);
      const oSig = normalizeOptionIds(f?.custom_feature_option_ids).join('-');
      const uSig = normalizeUploadIds(f?.custom_upload_ids).join('-');
      for (const it of items) {
        if (safeNumber(it?.product_id, null) === pid
          && normalizeOptionIds(it?.custom_feature_option_ids).join('-') === oSig
          && normalizeUploadIds(it?.custom_upload_ids).join('-') === uSig) {
          if (it?.id) dropIds.add(String(it.id));
        }
      }
    }
    if (!dropIds.size) return { discarded: 0 };
    const remaining = items.filter((it) => !dropIds.has(String(it?.id || '')));
    try {
      writeGuestCart({ version: 1, items: remaining, updated_at: Date.now() });
    } catch (_) {}
    writeMergeResult({ ...(result || {}), failed: 0, failures: [] });
    emitCartChanged();
    return { discarded: dropIds.size };
  },

  mergeGuestCartIntoServerCart: async () => {
    if (!hasValidAuthToken()) return { merged: false, reason: 'not_authenticated' };
    const guestItems = getGuestCartItems();
    if (!guestItems.length) return { merged: false, reason: 'empty_guest_cart' };

    const serverRes = await api.get('/orders/cart/');
    const serverItems = Array.isArray(serverRes?.data?.items) ? serverRes.data.items : [];
    const snapshot = {
      created_at: Date.now(),
      server_items: serverItems.map((it) => ({
        product_id: it?.product?.id ?? it?.product_id,
        quantity: it?.quantity,
        custom_feature_option_ids: it?.custom_feature_option_ids || [],
      })),
    };
    writeMergeBackup(snapshot);

    try {
      const tokensForUploadIds = (guestItem) => {
        const ids = normalizeUploadIds(guestItem?.custom_upload_ids);
        const meta = Array.isArray(guestItem?.custom_upload_meta) ? guestItem.custom_upload_meta : [];
        const byId = new Map();
        for (const m of meta) {
          const mid = Number(m?.id);
          if (Number.isFinite(mid) && m?.claim_token) byId.set(mid, String(m.claim_token));
        }
        return ids.map((id) => byId.get(Number(id)) || '');
      };
      const payloadItems = guestItems
        .map((it) => ({
          product_id: safeNumber(it?.product_id, null),
          quantity: Math.max(1, Math.floor(Number(it?.quantity) || 1)),
          custom_feature_option_ids: normalizeOptionIds(it?.custom_feature_option_ids),
          custom_upload_ids: normalizeUploadIds(it?.custom_upload_ids),
          custom_upload_tokens: tokensForUploadIds(it),
        }))
        .filter((it) => it.product_id && it.product_id > 0);

      const mergeKey = (it) => `${it.product_id}:${normalizeOptionIds(it.custom_feature_option_ids).join('-')}|${normalizeUploadIds(it.custom_upload_ids).join('-')}`;

      let res = await api.post('/orders/cart/merge-guest/', { items: payloadItems, atomic: true });
      let data = res?.data || {};
      let mergedKeys = new Set();
      let failures = Array.isArray(data?.failures) ? data.failures : [];

      // اگر ادغام atomic شکست خورد (مثلاً فایل‌های قدیمی مهمان با session منقضی)،
      // موارد خراب را کنار بگذار و بقیه را غیرatomic ادغام کن تا سبد کاربر از دست نرود
      if (Number(data?.failed) > 0 && failures.length) {
        const failedIdx = new Set(failures.map((f) => Number(f.index)));
        const retryItems = payloadItems.filter((_, i) => !failedIdx.has(i));
        if (retryItems.length) {
          try {
            const res2 = await api.post('/orders/cart/merge-guest/', { items: retryItems, atomic: false });
            const data2 = res2?.data || {};
            const okIdx = retryItems
              .map((it, i) => ({ it, i }))
              .filter(({ i }) => !(Array.isArray(data2.failures) && data2.failures.some((f) => Number(f.index) === i)))
              .map(({ it }) => it);
            okIdx.forEach((it) => mergedKeys.add(mergeKey(it)));
            failures = Array.isArray(data2.failures) ? data2.failures : [];
            data = { ...data2, merged: Number(data2.merged || 0), failed: failures.length };
          } catch (_) {}
        }
      } else if (Number(data?.merged) > 0) {
        payloadItems.forEach((it) => mergedKeys.add(mergeKey(it)));
      }

      // فقط آیتم‌های موفق از guestCart حذف شوند؛ موارد خراب برای تلاش مجدد بمانند
      if (mergedKeys.size > 0) {
        const remaining = guestItems.filter((it) => {
          const k = mergeKey({
            product_id: safeNumber(it?.product_id, null),
            custom_feature_option_ids: it?.custom_feature_option_ids,
            custom_upload_ids: it?.custom_upload_ids,
          });
          return !mergedKeys.has(k);
        });
        try {
          writeGuestCart({ version: 1, items: remaining, updated_at: Date.now() });
        } catch (_) {}
      }
      emitCartChanged();
      const result = {
        merged_at: Date.now(),
        total: payloadItems.length,
        merged: Number(data?.merged) || 0,
        failed: failures.length,
        failures,
        atomic: Boolean(data?.atomic),
      };
      writeMergeResult(result);
      return result;
    } catch (e) {
      const data = e?.response?.data || {};
      const failures = Array.isArray(data?.failures) ? data.failures : [];
      const result = {
        merged_at: Date.now(),
        total: guestItems.length,
        merged: 0,
        failed: failures.length || 1,
        failures: failures.length ? failures : [{ error: String(data?.error || e?.message || 'merge_failed') }],
        atomic: true,
      };
      writeMergeResult(result);
      emitCartChanged();
      return result;
    }
  },

  undoLastCartMerge: async () => {
    if (!hasValidAuthToken()) return { undone: false, reason: 'not_authenticated' };
    const backup = readMergeBackup();
    const items = Array.isArray(backup?.server_items) ? backup.server_items : [];
    if (!items.length) return { undone: false, reason: 'no_backup' };
    await api.delete('/orders/cart/clear/');
    const failures = [];
    for (const it of items) {
      try {
        await api.post('/orders/cart/add/', {
          product_id: it.product_id,
          quantity: it.quantity,
          ...(Array.isArray(it.custom_feature_option_ids) && it.custom_feature_option_ids.length
            ? { custom_feature_option_ids: it.custom_feature_option_ids }
            : {}),
        });
      } catch (e) {
        const message = e?.response?.data?.error || e?.message || 'restore_failed';
        failures.push({ ...it, error: String(message) });
      }
    }
    emitCartChanged();
    writeMergeBackup(null);
    writeMergeResult(null);
    return { undone: failures.length === 0, failures };
  },
};

// Wishlist API calls
export const wishlistAPI = {
  // Get user wishlist
  getWishlist: () => {
    return api.get('/products/wishlist/');
  },

  // Add to wishlist
  addToWishlist: (productId) => {
    return api.post('/products/wishlist/add/', { product_id: productId });
  },

  // Remove from wishlist
  removeFromWishlist: (productId) => {
    return api.delete(`/products/wishlist/remove/${productId}/`);
  }
};

// Orders API calls
export const ordersAPI = {
  // Get user orders
  getOrders: (paramsOrPage = undefined) => {
    if (paramsOrPage == null) return api.get('/orders/');
    if (typeof paramsOrPage === 'number') return api.get('/orders/', { params: { page: paramsOrPage } });
    const params = paramsOrPage && typeof paramsOrPage === 'object' ? paramsOrPage : undefined;
    return api.get('/orders/', params ? { params } : undefined);
  },

  // Get user orders (alias for compatibility)
  getUserOrders: (paramsOrPage = undefined) => {
    if (paramsOrPage == null) return api.get('/orders/');
    if (typeof paramsOrPage === 'number') return api.get('/orders/', { params: { page: paramsOrPage } });
    const params = paramsOrPage && typeof paramsOrPage === 'object' ? paramsOrPage : undefined;
    return api.get('/orders/', params ? { params } : undefined);
  },

  // Get single order
  getOrder: (id) => {
    return api.get(`/orders/${id}/`);
  },

  // Create order
  createOrder: (orderData, config = undefined) => {
    if (typeof FormData !== 'undefined' && orderData instanceof FormData) {
      return Promise.reject(new Error('multipart_not_allowed'));
    }
    const cfg = config && typeof config === 'object' ? { ...config } : {};
    if (cfg.timeout == null) cfg.timeout = 45000;
    return api.post('/orders/create/', orderData, cfg);
  },

  createPayment: (orderId, paymentData = {}, config = undefined) => {
    const cfg = config && typeof config === 'object' ? { ...config } : {};
    if (cfg.timeout == null) cfg.timeout = 65000;
    return api.post(`/orders/${orderId}/payment/`, paymentData, cfg);
  },

  cancelOrder: (orderId, config = undefined) => {
    const cfg = config && typeof config === 'object' ? { ...config } : {};
    if (cfg.timeout == null) cfg.timeout = 30000;
    return api.post(`/orders/${orderId}/cancel/`, {}, cfg);
  },

  getShippingOptions: (addressId = undefined) => {
    const a = Number(addressId);
    const params = Number.isFinite(a) && a > 0 ? { address_id: a } : undefined;
    return api.get('/orders/shipping/options/', params ? { params } : undefined);
  },
  getScheduledAvailability: (date, slot = undefined, addressId = undefined) => {
    const d = String(date || '').trim();
    const s = String(slot || '').trim();
    const a = Number(addressId);
    const params = s ? { date: d, slot: s } : { date: d };
    if (Number.isFinite(a) && a > 0) params.address_id = a;
    return api.get('/orders/shipping/scheduled/availability/', { params });
  },
  getScheduledSlots: (date, addressId = undefined) => {
    const d = String(date || '').trim();
    const a = Number(addressId);
    const params = { date: d };
    if (Number.isFinite(a) && a > 0) params.address_id = a;
    return api.get('/orders/shipping/scheduled/slots/', { params });
  },

  // Apply coupon
  applyCoupon: (couponData) => {
    return api.post('/orders/coupons/apply/', couponData);
  },

  getMyCoupons: () => {
    return api.get('/orders/coupons/my/');
  },

  sellerListCouriers: () => {
    return api.get('/orders/seller/couriers/');
  },
  sellerListCourierItems: () => {
    return api.get('/orders/seller/courier-items/');
  },
  sellerDashboard: () => {
    return api.get('/orders/seller/dashboard/');
  },
  sellerOrderItems: () => {
    return api.get('/orders/seller/orders/');
  },
  sellerOrderFilesZip: (orderId) => {
    const id = Number(orderId);
    if (!Number.isFinite(id) || id <= 0) return Promise.reject(new Error('invalid_order_id'));
    return api.get(`/orders/seller/orders/${id}/files.zip/`, { responseType: 'blob' });
  },
  sellerAssignCourier: (itemId, courierId) => {
    const id = Number(itemId);
    const cid = Number(courierId);
    if (!Number.isFinite(id) || id <= 0) return Promise.reject(new Error('invalid_item_id'));
    if (!Number.isFinite(cid) || cid <= 0) return Promise.reject(new Error('invalid_courier_id'));
    return api.post(`/orders/seller/courier-items/${id}/assign/`, { courier_id: cid });
  },

  courierAssignments: () => {
    return api.get('/orders/courier/assignments/');
  },
  courierHistory: (params = {}) => {
    const p = params && typeof params === 'object' ? params : {};
    return api.get('/orders/courier/history/', { params: p });
  },
  courierConfirmReceived: (itemId) => {
    const id = Number(itemId);
    if (!Number.isFinite(id) || id <= 0) return Promise.reject(new Error('invalid_item_id'));
    return api.post(`/orders/courier/assignments/${id}/confirm/`, {});
  },
};

export const staffShippingOpsAPI = {
  getCapacityDashboard: (params = {}) => {
    const p = params && typeof params === 'object' ? params : {};
    return api.get('/orders/shipping/admin/capacity/', { params: p });
  },
  getCapacityAlerts: (params = {}) => {
    const p = params && typeof params === 'object' ? params : {};
    return api.get('/orders/shipping/admin/alerts/', { params: p });
  },
  getCourierSettings: () => {
    return api.get('/orders/shipping/admin/courier-settings/');
  },
  updateCourierSettings: (payload = {}) => {
    const body = payload && typeof payload === 'object' ? payload : {};
    return api.post('/orders/shipping/admin/courier-settings/', body);
  },
  exportScheduleCsv: (params = {}) => {
    const p = params && typeof params === 'object' ? params : {};
    return api.get('/orders/shipping/admin/export/', { params: p, responseType: 'blob' });
  },
  verifyDeliveryCode: (payload = {}) => {
    const body = payload && typeof payload === 'object' ? payload : {};
    return api.post('/orders/delivery/verify/', body);
  },
  getOrderLookup: (params = {}) => {
    const p = params && typeof params === 'object' ? params : {};
    return api.get('/orders/staff/orders/lookup/', { params: p });
  },
  staffOrderFileDownload: (fileId) => {
    const id = Number(fileId);
    if (!Number.isFinite(id) || id <= 0) return Promise.reject(new Error('invalid_file_id'));
    return api.get(`/orders/staff/orders/files/${id}/download/`, { responseType: 'blob' });
  },
  moveOrderToAwaitingCourierPickup: ({ order_number, order_id } = {}) => {
    const orderNumber = String(order_number || '').trim();
    const orderId = order_id === null || order_id === undefined ? null : Number(order_id);
    if (!orderNumber && !(Number.isFinite(orderId) && orderId > 0)) {
      return Promise.reject(new Error('missing_order_identifier'));
    }
    const body = {
      ...(orderNumber ? { order_number: orderNumber } : {}),
      ...(Number.isFinite(orderId) && orderId > 0 ? { order_id: orderId } : {}),
    };
    return api.post('/orders/staff/orders/move-to-awaiting-courier/', body);
  },
  courierAssignments: () => {
    return api.get('/orders/courier/assignments/');
  },
  courierConfirmReceived: (itemId) => {
    const id = Number(itemId);
    if (!Number.isFinite(id) || id <= 0) return Promise.reject(new Error('invalid_item_id'));
    return api.post(`/orders/courier/assignments/${id}/confirm/`, {});
  },
  getAwaitingCourierDashboard: (params = {}) => {
    const p = params && typeof params === 'object' ? params : {};
    return api.get('/orders/shipping/admin/awaiting-courier/', { params: p });
  },
  listCourierCandidates: () => {
    return api.get('/orders/shipping/admin/couriers/');
  },
  assignCourierToAwaitingCourierItem: (itemId, courierId) => {
    const id = Number(itemId);
    const cid = Number(courierId);
    if (!Number.isFinite(id) || id <= 0) return Promise.reject(new Error('invalid_item_id'));
    if (!Number.isFinite(cid) || cid <= 0) return Promise.reject(new Error('invalid_courier_id'));
    return api.post(`/orders/shipping/admin/awaiting-courier/items/${id}/assign/`, { courier_id: cid });
  },
  forceExpireAwaitingCourierItem: (itemId) => {
    const id = Number(itemId);
    if (!Number.isFinite(id) || id <= 0) return Promise.reject(new Error('invalid_item_id'));
    return api.post(`/orders/shipping/admin/awaiting-courier/items/${id}/force-expire/`, {});
  },
  getAwaitingCourierItemHistory: (itemId) => {
    const id = Number(itemId);
    if (!Number.isFinite(id) || id <= 0) return Promise.reject(new Error('invalid_item_id'));
    return api.get(`/orders/shipping/admin/awaiting-courier/items/${id}/history/`);
  },
};

export const couponAPI = {
  validate: (code, extraPayload = {}) => {
    const c = String(code || '').trim();
    if (!c) return Promise.reject(new Error('missing_coupon_code'));
    const extra = extraPayload && typeof extraPayload === 'object' ? extraPayload : {};
    const payload = { ...extra, coupon_code: c };
    return api
      .post('/orders/coupons/apply/', payload)
      .then((res) => {
        const snapshot = {
          code: c,
          shipping_method: extra?.shipping_method || null,
          validated_at: Date.now(),
          ...(res?.data || {}),
        };
        couponStore.setCode(c);
        couponStore.setSnapshot(snapshot);
        return res;
      });
  },
  clear: () => {
    couponStore.clear();
  },
};

export const analyticsAPI = {
  logActivity: ({ action, description = '' }) => {
    const a = String(action || '').trim();
    if (!a) return Promise.reject(new Error('missing_action'));
    return api.post('/analytics/api/log-activity/', { action: a, description: String(description || '') });
  },
};

// Chat API calls
export const chatAPI = {
  // Get chat rooms (customer-side: /chats فرانت فقط space مشتری است)
  getChats: (params = {}) => {
    return api.get('/chat/rooms/', { params: { as: 'customer', ...params } });
  },

  // Get room info (customer-side)
  getRoomInfo: (roomId, params = {}) => {
    return api.get(`/chat/rooms/${roomId}/`, { params });
  },

  // Get messages for a room (customer-side)
  getMessages: (roomId, params = {}) => {
    const { limit = 20, cursor, as: asScope, ...rest } = params;
    const query = { limit, ...rest };
    if (asScope) query.as = asScope;
    if (cursor) query.cursor = cursor;
    return api.get(`/chat/messages/${roomId}/`, { params: query });
  },

  // Send message
  sendMessage: (messageData) => {
    return api.post('/chat/messages/', messageData);
  },

  // Create new chat room
  createRoom: (roomData) => {
    return api.post('/chat/create-room/', roomData);
  },

  // Start new conversation for custom product
  startCustomProductChat: (productId) => {
    return api.post('/chat/create-room/', {
      product_id: productId,
      room_type: 'product'
    });
  },

  // Upload file
  uploadFile: (formData, config = {}) => {
    return api.post('/chat/upload/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      ...config,
    });
  },

  // Mark messages as read in a room
  markRead: ({ room_id, message_ids }) => {
    return api.post('/chat/messages/read/', { room_id, message_ids });
  },

  // درخواست افزایش حجم اتاق (توسط مشتری)
  requestSizeIncrease: (roomId, data = {}) => {
    return api.post(`/chat/rooms/${roomId}/request-size-increase/`, data);
  }
};

// Staff Chat API calls (support/admin only)
export const staffChatAPI = {
  // List all active chat rooms (staff-space: seller_id=me per Phase 1 scope)
  getRooms: (params = {}) => {
    return api.get('/staff/chat/rooms/', { params: { as: 'staff', ...params } });
  },

  // Get messages for a specific room (staff view)
  getMessages: (roomId, params = {}) => {
    const { limit = 50 } = params;
    return api.get(`/staff/chat/messages/${roomId}/`, { params: { limit } });
  },

  // Create internal note in a room
  createNote: (roomId, content) => {
    return api.post(`/staff/chat/messages/${roomId}/note`, { content });
  },

  // Get transcript of a room
  getTranscript: (roomId, params = {}) => {
    const { format = 'json', include_internal = true } = params;
    return api.get(`/staff/chat/rooms/${roomId}/transcript`, { params: { format, include_internal } });
  },

  // Phase 16-E — on-demand ZIP export (admin-only).
  // params: { room_ids?: string, seller_id?: string, include_media?: boolean }
  exportZip: (params = {}) => {
    const { room_ids, seller_id, include_media = true } = params;
    const p = { include_media: include_media ? '1' : '0' };
    if (room_ids) p.room_ids = room_ids;
    if (seller_id) p.seller_id = String(seller_id);
    return api.get('/staff/chat/export/', {
      params: p,
      responseType: 'blob',
    });
  },

  // Assign a room to a staff user (or "me")
  assignRoom: (roomId, assigned_to = 'me') => {
    return api.put(`/staff/chat/rooms/${roomId}/assign`, { assigned_to });
  },

  // Release a room (unassign)
  releaseRoom: (roomId) => {
    return api.put(`/staff/chat/rooms/${roomId}/release`);
  },

  // Close a room
  closeRoom: (roomId) => {
    return api.put(`/staff/chat/rooms/${roomId}/close`);
  },

  // Reopen a room
  reopenRoom: (roomId) => {
    return api.put(`/staff/chat/rooms/${roomId}/reopen`);
  },

  // Set status explicitly (pending|open|closed)
  setStatus: (roomId, status) => {
    return api.put(`/staff/chat/rooms/${roomId}/status`, { status });
  },
  // Set tags on room
  setRoomTags: (roomId, tags) => {
    return api.put(`/staff/chat/rooms/${roomId}/tags`, { tags });
  },

  // Tags catalog CRUD
  getTags: () => {
    return api.get('/staff/chat/tags/');
  },
  createTag: ({ name, color }) => {
    return api.post('/staff/chat/tags/', { name, color });
  },
  updateTag: (id, { name, color }) => {
    return api.put(`/staff/chat/tags/${id}/`, { name, color });
  },
  deleteTag: (id) => {
    return api.delete(`/staff/chat/tags/${id}/`);
  },

  // Canned replies CRUD and insert
  getCannedReplies: (params = {}) => {
    return api.get('/staff/chat/canned-replies/', { params });
  },
  createCannedReply: ({ title, content, category }) => {
    return api.post('/staff/chat/canned-replies/', { title, content, category });
  },
  updateCannedReply: (id, { title, content, category }) => {
    return api.put(`/staff/chat/canned-replies/${id}/`, { title, content, category });
  },
  deleteCannedReply: (id) => {
    return api.delete(`/staff/chat/canned-replies/${id}/`);
  },
  insertCannedReply: (id, roomId) => {
    return api.post(`/staff/chat/canned-replies/${id}/insert/${roomId}/`);
  },
  // Metrics summary
  getMetricsSummary: (params = {}) => {
    return api.get('/staff/chat/metrics/summary', { params });
  },
  // Export metrics as CSV
  exportMetricsCSV: (params = {}) => {
    // P3 (بند ۴.۸): `format` در DRF پارامتر رزروِ content negotiation است و
    // `?format=csv` با ۴۰۴ رد می‌شد؛ نام پارامتر به `export` عوض شده.
    return api.get('/staff/chat/metrics/summary', {
      params: { ...params, export: 'csv' },
      responseType: 'blob'
    });
  },
  // درخواستهای افزایش حجم اتاق (لیست)
  getSizeRequests: (roomId, params = {}) => {
    return api.get(`/staff/chat/rooms/${roomId}/size-requests/`, { params });
  },
  // تایید/رد درخواست افزایش حجم
  reviewSizeRequest: (requestId, data) => {
    return api.post(`/staff/chat/size-requests/${requestId}/review`, data);
  },
  // افزایش مستقیم سقف حجم اتاق (۱۵۰/۳۰۰/۵۰۰ مگابایت)
  increaseRoomSize: (roomId, max_room_bytes) => {
    return api.post(`/staff/chat/rooms/${roomId}/increase-size/`, { max_room_bytes });
  },
  // فید مرکزی پیامهای سیستمی و درخواستها (با فیلتر)
  getSystemFeed: (params = {}) => {
    return api.get('/staff/chat/system-feed/', { params });
  },
  // تعداد خودکار درخواستهای در انتظار (بدون نیاز به کلیک)
  getPendingSizeRequestCounts: (params = {}) => {
    return api.get('/staff/chat/size-requests/pending-count/', { params });
  },
  // علامت زدن درخواستهای یک اتاق به عنوان مشاهده شده (کاهش شمارنده بدون تایید/رد)
  markSizeRequestsViewed: (roomId) => {
    return api.post('/staff/chat/size-requests/mark-viewed/', { room_id: roomId });
  }
};

// Settings API calls
export const settingsAPI = {
  getSystem: () => api.get('/settings/system/'),
  // P2 review fix: مسیر قبلی (/settings/system/public/) در بک‌اند وجود
  // نداشت (404) — مسیر درست همان public system است.
  getSystemSettings: () => api.get('/settings/system/'),
  getPremiumServices: () => api.get('/settings/premium-services/'),
  sendContactMessage: ({ name, email, message }) => {
    return api.post('/settings/contact/', { name, email, message });
  },
  // Staff manage system settings (header, loop bar)
  getSystemManage: () => {
    return api.get('/settings/system/manage/');
  },
  updateSystemManage: (payload) => {
    return api.patch('/settings/system/manage/', payload);
  },
  canManageBanners: () => {
    try {
      const token = typeof window !== 'undefined' ? getAccessToken() : null;
      if (!token) return false;
      const raw = typeof window !== 'undefined' ? getUserBlob() : null;
      const u = raw ? JSON.parse(raw) : null;
      if (u?.is_staff !== true) return false;
      // P3 (بند ۴.۶): تشخیص نقش از utils/roles (فهرست دستی roles/user_type حذف شد)
      return hasRole(u, 'admin') || hasRole(u, 'support');
    } catch (_) {
      return false;
    }
  },
  getBannersManage: () => {
    if (!settingsAPI.canManageBanners()) return Promise.resolve({ data: { results: [] } });
    return api.get('/settings/banners/manage/');
  },
  createBanner: (formData) => {
    if (!settingsAPI.canManageBanners()) return Promise.reject(new Error('forbidden'));
    const isForm = typeof FormData !== 'undefined' && formData instanceof FormData;
    if (isForm) {
      return api.post('/settings/banners/manage/', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    }
    return api.post('/settings/banners/manage/', formData);
  },
  updateBanner: (id, formData) => {
    if (!settingsAPI.canManageBanners()) return Promise.reject(new Error('forbidden'));
    const isForm = typeof FormData !== 'undefined' && formData instanceof FormData;
    if (isForm) {
      return api.put(`/settings/banners/manage/${id}/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    }
    return api.put(`/settings/banners/manage/${id}/`, formData);
  },
  deleteBanner: (id) => {
    if (!settingsAPI.canManageBanners()) return Promise.reject(new Error('forbidden'));
    return api.delete(`/settings/banners/manage/${id}/`);
  },
};

export const notificationsAPI = {
  list: (params = {}) => {
    return api.get('/notifications/', { params });
  },
  getSettings: () => {
    return api.get('/notifications/settings/');
  },
  updateSettings: (payload) => {
    return api.patch('/notifications/settings/', payload);
  },
  getVapidPublicKey: () => {
    return api.get('/notifications/push/vapid-public-key/');
  },
  subscribePush: (subscription) => {
    return api.post('/notifications/push/subscribe/', subscription);
  },
  unsubscribePush: (endpoint) => {
    return api.post('/notifications/push/unsubscribe/', { endpoint });
  },
  markAsRead: (notificationIds = []) => {
    return api.post('/notifications/mark-as-read/', { notification_ids: notificationIds });
  },
};

// Support API calls
export const supportAPI = {
  getActivePurchaseTerms: () => api.get('/support/purchase-terms/active/'),
};

export const tempMediaAPI = {
  upload: (file, { kind = '', draft_key = '', onUploadProgress } = {}) => {
    const fd = new FormData();
    fd.append('file', file);
    if (kind) fd.append('kind', kind);
    if (draft_key) fd.append('draft_key', draft_key);
    return api.post('/support/temp-media/upload/', fd, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      ...(typeof onUploadProgress === 'function' ? { onUploadProgress } : {}),
    });
  },
  delete: (id) => {
    return api.delete(`/support/temp-media/${id}/delete/`);
  },
};

// User API calls
export const userAPI = {
  // Get user profile
  getProfile: () => {
    return api.get('/auth/profile/');
  },

  // Update user profile
  updateProfile: (profileData) => {
    return api.patch('/auth/profile/', profileData);
  },

  getAddresses: () => {
    return api.get('/auth/addresses/');
  },

  createAddress: (addressData) => {
    return api.post('/auth/addresses/', addressData);
  },

  updateAddress: (id, addressData) => {
    return api.put(`/auth/addresses/${id}/`, addressData);
  },

  deleteAddress: (id) => {
    return api.delete(`/auth/addresses/${id}/`);
  },

  // Change password
  changePassword: (passwordData) => {
    return api.post('/auth/change-password/', passwordData);
  },

  // Upload profile picture
  uploadProfilePicture: (formData) => {
    return api.patch('/auth/profile/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  }
};

// Wallet (customer self-service) API calls
export const financeWalletAPI = {
  // Read-only status of the customer's own wallet (balance + whether
  // self-charge is enabled by admin).
  getStatus: () => api.get('/finance/wallet/status/'),
  // Charge the customer's own wallet (only works when admin has enabled it).
  charge: (payload) => api.post('/finance/wallet/', payload),
  // Recent wallet transactions (their own, via the staff-gated viewset is not
  // accessible to customers — so we use the wallet GET's nested transactions).
  get: () => api.get('/finance/wallet/'),
};

// Referral (دعوت دوستان) API calls
export const referralAPI = {
  // My referral code + stats + invitees
  me: () => api.get('/finance/referral/me/'),
  // Activate my referral code (after first purchase)
  activate: () => api.post('/finance/referral/activate/'),
  // Apply an inviter's code to my account
  apply: (code) => api.post('/finance/referral/apply/', { code }),
};

// Staff settlement (تسویه) API calls
export const financeSettlementAPI = {
  // Bank account (GET/PUT my own)
  getBankAccount: () => api.get('/finance/bank-accounts/'),
  createBankAccount: (data) => api.post('/finance/bank-accounts/', data),
  // Payout requests
  listMyRequests: () => api.get('/finance/payout-requests/'),
  createRequest: (data) => api.post('/finance/payout-requests/', data),
};

// Partner (collaboration requests) API calls
export const partnerAPI = {
  // Create a collaboration request
  create: (data) => api.post('/partners/list/', data),

  // List my collaboration requests
  getMyRequests: () => api.get('/partners/list/'),

  // Get a single request detail
  getDetail: (pk) => api.get(`/partners/${pk}/`),

  // Withdraw a pending request
  withdraw: (pk) => api.post(`/partners/${pk}/withdraw/`),

  // Upload a media file (image/video) to a request
  uploadMedia: (pk, file, { onUploadProgress } = {}) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/partners/${pk}/media/`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      ...(typeof onUploadProgress === 'function' ? { onUploadProgress } : {}),
    });
  },

  // S4: Delete a media file (owner, pending/under_review only)
  deleteMedia: (pk, mediaPk) => api.delete(`/partners/${pk}/media/${mediaPk}/`),

  // P8-A: Upload an ID document (national_id / national_card) to a request
  uploadDocument: (pk, file, docType, { onUploadProgress } = {}) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('doc_type', docType);
    return api.post(`/partners/${pk}/documents/`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      ...(typeof onUploadProgress === 'function' ? { onUploadProgress } : {}),
    });
  },

  // P8-A: Delete an ID document (owner, pending/under_review only)
  deleteDocument: (pk, docPk) => api.delete(`/partners/${pk}/documents/${docPk}/`),
};

// Error handler utility
export const handleAPIError = (error) => {
  if (error.response) {
    // Server responded with error status
    // P3 review fix: کلید error هم چک شود — بک‌اند {error: "..."} برمی‌گرداند
    const message = error.response.data?.error || error.response.data?.message || error.response.data?.detail || 'خطایی رخ داده است';
    return {
      message,
      status: error.response.status,
      data: error.response.data
    };
  } else if (error.request) {
    // Request made but no response
    return {
      message: 'خطا در برقراری ارتباط با سرور',
      status: null,
      data: null
    };
  } else {
    // Something else happened
    return {
      message: error.message || 'خطای غیرمنتظره',
      status: null,
      data: null
    };
  }
};

export default api;
